import { MapPin, Calendar, Users, Star } from "lucide-react";
import type React from "react";
import { Link } from "react-router-dom";

import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { useGetFeaturedEventsQuery, useGetUpcomingEventsQuery } from "../../store/api/eventApi";
import { useGetGroundsQuery } from "../../store/api/groundApi";
import { ROUTES } from "../../utils/constants";
import { formatDate, formatCurrency } from "../../utils/helpers";

const Home: React.FC = () => {
  const { data: featuredEvents } = useGetFeaturedEventsQuery();
  const { data: upcomingEvents } = useGetUpcomingEventsQuery({ limit: 6 });
  const { data: grounds } = useGetGroundsQuery({ limit: 8 });

  return (
    <div className='min-h-screen'>
      {/* Hero Section */}
      <section className='relative bg-gradient-to-r from-blue-600 to-purple-600 text-white'>
        <div className='container py-24 md:py-32'>
          <div className='max-w-3xl'>
            <h1 className='text-4xl font-bold tracking-tight md:text-6xl'>
              Book Your Perfect Ground
            </h1>
            <p className='mt-6 text-lg text-blue-100 md:text-xl'>
              Discover and book premium sports grounds for your events. From cricket stadiums to
              soccer fields, find the perfect venue for your next game.
            </p>
            <div className='mt-8 flex flex-col gap-4 sm:flex-row'>
              <Button size='lg' asChild className='bg-white text-blue-600 hover:bg-gray-100'>
                <Link to={ROUTES.GROUNDS}>Browse Grounds</Link>
              </Button>
              <Button
                size='lg'
                variant='outline'
                asChild
                className='border-white bg-transparent text-white hover:bg-white hover:text-blue-600'
              >
                <Link to={ROUTES.EVENTS}>View Events</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Events */}
      {featuredEvents?.data && featuredEvents.data.length > 0 && (
        <section className='py-16'>
          <div className='container'>
            <div className='mb-8 flex items-center justify-between'>
              <div>
                <h2 className='text-3xl font-bold'>Featured Events</h2>
                <p className='mt-2 text-muted-foreground'>
                  Don&apos;t miss these exciting upcoming events
                </p>
              </div>
              <Button variant='outline' asChild>
                <Link to={ROUTES.EVENTS}>View All Events</Link>
              </Button>
            </div>
            <div className='grid gap-6 md:grid-cols-2 lg:grid-cols-3'>
              {featuredEvents.data.slice(0, 3).map(event => (
                <Card key={event.id} className='overflow-hidden'>
                  <div className='aspect-video bg-gradient-to-r from-blue-500 to-purple-500' />
                  <CardHeader>
                    <div className='flex items-center justify-between'>
                      <Badge variant='secondary'>
                        <Star className='mr-1 h-3 w-3' />
                        Featured
                      </Badge>
                      <span className='text-sm text-muted-foreground'>
                        {formatDate(event.datetime)}
                      </span>
                    </div>
                    <CardTitle className='line-clamp-2'>{event.title}</CardTitle>
                    <CardDescription className='line-clamp-2'>{event.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className='flex items-center justify-between'>
                      <div className='flex items-center text-sm text-muted-foreground'>
                        <MapPin className='mr-1 h-4 w-4' />
                        {event.ground?.location.city}
                      </div>
                      <Button size='sm' asChild>
                        <Link to={`/events/${event.id}`}>View Details</Link>
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Popular Grounds */}
      {grounds?.data && grounds.data.length > 0 && (
        <section className='bg-muted/50 py-16'>
          <div className='container'>
            <div className='mb-8 flex items-center justify-between'>
              <div>
                <h2 className='text-3xl font-bold'>Popular Grounds</h2>
                <p className='mt-2 text-muted-foreground'>Discover top-rated venues in your area</p>
              </div>
              <Button variant='outline' asChild>
                <Link to={ROUTES.GROUNDS}>View All Grounds</Link>
              </Button>
            </div>
            <div className='grid gap-6 md:grid-cols-2 lg:grid-cols-4'>
              {grounds.data.slice(0, 4).map(ground => (
                <Card key={ground.id} className='overflow-hidden'>
                  <div className='aspect-video bg-gradient-to-r from-green-500 to-blue-500' />
                  <CardHeader className='pb-3'>
                    <CardTitle className='line-clamp-1'>{ground.name}</CardTitle>
                    <CardDescription className='flex items-center'>
                      <MapPin className='mr-1 h-3 w-3' />
                      {ground.location.city}, {ground.location.country}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className='flex items-center justify-between'>
                      <div className='flex items-center text-sm text-muted-foreground'>
                        <Users className='mr-1 h-4 w-4' />
                        {ground.capacity.toLocaleString()}
                      </div>
                      <div className='text-sm font-medium'>
                        {formatCurrency(ground.pricing.basePrice, ground.pricing.currency)}
                      </div>
                    </div>
                    <Button size='sm' className='mt-3 w-full' asChild>
                      <Link to={`/grounds/${ground.id}`}>View Details</Link>
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Upcoming Events */}
      {upcomingEvents?.data && upcomingEvents.data.length > 0 && (
        <section className='py-16'>
          <div className='container'>
            <div className='mb-8 flex items-center justify-between'>
              <div>
                <h2 className='text-3xl font-bold'>Upcoming Events</h2>
                <p className='mt-2 text-muted-foreground'>
                  Book your tickets for these exciting events
                </p>
              </div>
              <Button variant='outline' asChild>
                <Link to={ROUTES.EVENTS}>View All Events</Link>
              </Button>
            </div>
            <div className='grid gap-4 md:grid-cols-2 lg:grid-cols-3'>
              {upcomingEvents.data.map(event => (
                <Card key={event.id}>
                  <CardHeader className='pb-3'>
                    <div className='flex items-center justify-between'>
                      <Badge variant='outline'>
                        <Calendar className='mr-1 h-3 w-3' />
                        {formatDate(event.datetime)}
                      </Badge>
                      <Badge className='capitalize'>{event.ground?.type}</Badge>
                    </div>
                    <CardTitle className='line-clamp-2'>{event.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className='flex items-center justify-between'>
                      <div className='text-sm text-muted-foreground'>
                        {event.ground?.location.city}
                      </div>
                      <Button size='sm' asChild>
                        <Link to={`/events/${event.id}`}>Book Now</Link>
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default Home;
